// ============================================================
// API SERVICE — Connects admin panel to the Flask backend
// All calls go through here. Falls back to mock data on error.
// ============================================================

import { API_BASE_URL, AUTH_TOKEN_KEY } from '../utils/constants';

// ── HTTP Client ───────────────────────────────────────────────
const getHeaders = () => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const request = async (method, path, body = null) => {
  const opts = { method, headers: getHeaders() };
  if (body) opts.body = JSON.stringify(body);
  let res;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, opts);
  } catch (networkErr) {
    // Truly unreachable — backend is down
    throw new Error('NETWORK_ERROR: ' + networkErr.message);
  }
  if (res.status === 401) {
    // Token expired or invalid — clear storage and redirect to login (only if not already there)
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem('biryani_admin_user');
    if (!window.location.pathname.includes('/admin/login')) {
      window.location.replace('/admin/login');
    }
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(err.message || `HTTP ${res.status}`);
  }
  const json = await res.json();
  // Unwrap Flask success envelope: { success: true, data: {...} }
  return json?.data !== undefined ? json.data : json;
};

export const api = {
  get:    (path)         => request('GET',    path),
  post:   (path, body)   => request('POST',   path, body),
  put:    (path, body)   => request('PUT',    path, body),
  patch:  (path, body)   => request('PATCH',  path, body),
  delete: (path)         => request('DELETE', path),
};

// ── Wrapper with mock fallback ────────────────────────────────
// Only falls back to mock if the backend is genuinely unreachable (network error).
// Auth errors (401) redirect to login. Server errors (500) throw so the page shows an error.
const withFallback = async (apiFn, mockFn) => {
  try {
    return await apiFn();
  } catch (e) {
    if (e.message && e.message.startsWith('NETWORK_ERROR')) {
      console.warn('[API] Backend unreachable — using mock data:', e.message);
      return typeof mockFn === 'function' ? mockFn() : mockFn;
    }
    // Re-throw auth / server errors so pages know something went wrong
    throw e;
  }
};

// ── Admin Auth ────────────────────────────────────────────────
export const adminApi = {
  login: (username, password) =>
    api.post('/admin/auth/login', { username, password }),

  // ── Dashboard ──────────────────────────────────────────────
  getDashboard: (mockFn) =>
    withFallback(() => api.get('/admin/dashboard'), mockFn),

  // ── Takeaway Orders ────────────────────────────────────────
  getOrders: (params = {}, mockFn) => {
    const q = new URLSearchParams(params).toString();
    return withFallback(() => api.get(`/admin/orders${q ? '?' + q : ''}`), mockFn);
  },

  getOrderById: (id) => api.get(`/admin/orders/${id}`),

  updateOrderStatus: (id, status, reason = '') =>
    api.patch(`/admin/orders/${id}/status`, { status, reason }),

  // ── Pickup Queue ───────────────────────────────────────────
  getPickupQueue: (restaurantId = null, mockFn) => {
    const path = restaurantId ? `/admin/pickup-queue?restaurant_id=${restaurantId}` : '/admin/pickup-queue';
    return withFallback(() => api.get(path), mockFn);
  },

  markPickedUp: (orderId) =>
    api.patch(`/admin/orders/${orderId}/status`, { status: 'completed' }),

  // ── Restaurants ────────────────────────────────────────────
  getRestaurants: (params = {}, mockFn) => {
    const q = new URLSearchParams(params).toString();
    return withFallback(() => api.get(`/admin/restaurants${q ? '?' + q : ''}`), mockFn);
  },

  getRestaurantById: (id) => api.get(`/admin/restaurants/${id}`),

  toggleRestaurantStatus: (id, status) =>
    api.patch(`/admin/restaurants/${id}/status`, { status }),

  getRestaurantMenu: (kitchenId, mockFn) =>
    withFallback(() => api.get(`/admin/restaurants/${kitchenId}/menu`), mockFn),

  toggleMenuItemAvailability: (kitchenId, itemId, isAvailable) =>
    api.patch(`/admin/restaurants/${kitchenId}/menu/${itemId}`, { is_available: isAvailable }),

  getKitchenUsers: (kitchenId) =>
    api.get(`/admin/restaurants/${kitchenId}/users`),

  // ── Restaurant Creation (Admin Wizard) ─────────────────────
  createRestaurant: (data) =>
    api.post('/admin/restaurants', data),

  updateRestaurant: (id, data) =>
    api.put(`/admin/restaurants/${id}`, data),

  deleteRestaurant: (id) =>
    api.delete(`/admin/restaurants/${id}`),

  activateRestaurant: (id) =>
    api.patch(`/admin/restaurants/${id}/activate`),

  suspendRestaurant: (id, reason = '') =>
    api.patch(`/admin/restaurants/${id}/suspend`, { reason }),

  uploadRestaurantImage: (id, imageType, file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('image_type', imageType);
    const token = localStorage.getItem('biryani_admin_token');
    return fetch(`${require('../utils/constants').API_BASE_URL}/admin/restaurants/${id}/images`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    }).then(r => r.json()).then(j => j?.data ?? j);
  },

  uploadRestaurantDocument: (id, docType, file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('doc_type', docType);
    const token = localStorage.getItem('biryani_admin_token');
    return fetch(`${require('../utils/constants').API_BASE_URL}/admin/restaurants/${id}/documents`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    }).then(r => r.json()).then(j => j?.data ?? j);
  },

  generateRestaurantSlots: (id, params) =>
    api.post(`/admin/restaurants/${id}/slots/generate`, params),

  createRestaurantStaff: (id, staffData) =>
    api.post(`/admin/restaurants/${id}/staff`, staffData),

  // ── Customers ──────────────────────────────────────────────
  getCustomers: (params = {}, mockFn) => {
    const q = new URLSearchParams(params).toString();
    return withFallback(() => api.get(`/admin/customers${q ? '?' + q : ''}`), mockFn);
  },

  toggleCustomerStatus: (id, status) =>
    api.patch(`/admin/customers/${id}/status`, { status }),

  // ── Chefs / Kitchen Staff ──────────────────────────────────
  getChefs: (params = {}, mockFn) => {
    const q = new URLSearchParams(params).toString();
    return withFallback(() => api.get(`/admin/chefs${q ? '?' + q : ''}`), mockFn);
  },

  updateChefStatus: (id, status) =>
    api.patch(`/admin/chefs/${id}/status`, { status }),

  // ── Users / Admin Users ────────────────────────────────────
  getAdminUsers: (mockFn) =>
    withFallback(() => api.get('/admin/users'), mockFn),

  createAdminUser: (data) => api.post('/admin/users', data),
  updateAdminUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteAdminUser: (id) => api.delete(`/admin/users/${id}`),

  // ── Support Tickets ────────────────────────────────────────
  getSupportTickets: (params = {}, mockFn) => {
    const q = new URLSearchParams(params).toString();
    return withFallback(() => api.get(`/admin/support${q ? '?' + q : ''}`), mockFn);
  },

  replyToTicket: (id, message) =>
    api.post(`/admin/support/${id}/reply`, { message }),

  resolveTicket: (id) =>
    api.patch(`/admin/support/${id}/resolve`),

  // ── Legal Verification ─────────────────────────────────────
  getLegalVerifications: (mockFn) =>
    withFallback(() => api.get('/admin/legal'), mockFn),

  approveLegal: (id) => api.patch(`/admin/legal/${id}/approve`),
  rejectLegal:  (id, reason) => api.patch(`/admin/legal/${id}/reject`, { reason }),

  // ── Food Compliance ────────────────────────────────────────
  getFoodCompliance: (mockFn) =>
    withFallback(() => api.get('/admin/compliance'), mockFn),

  verifyCompliance: (id) => api.patch(`/admin/compliance/${id}/verify`),

  // ── Analytics & Reports ────────────────────────────────────
  getRevenueAnalytics: (period = 'monthly', mockFn) =>
    withFallback(() => api.get(`/admin/analytics/revenue?period=${period}`), mockFn),

  getOrderAnalytics: (period = 'weekly', mockFn) =>
    withFallback(() => api.get(`/admin/analytics/orders?period=${period}`), mockFn),

  getPickupAnalytics: (mockFn) =>
    withFallback(() => api.get('/admin/analytics/pickup'), mockFn),

  getPreparationAnalytics: (mockFn) =>
    withFallback(() => api.get('/admin/analytics/preparation'), mockFn),

  getPeakHours: (mockFn) =>
    withFallback(() => api.get('/admin/analytics/peak-hours'), mockFn),

  getSalesReport: (params = {}, mockFn) => {
    const q = new URLSearchParams(params).toString();
    return withFallback(() => api.get(`/admin/reports/sales${q ? '?' + q : ''}`), mockFn);
  },

  exportReport: (type, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/admin/reports/${type}/export${q ? '?' + q : ''}`);
  },

  // ── Finance ────────────────────────────────────────────────
  getCommissionData: (mockFn) =>
    withFallback(() => api.get('/admin/commission'), mockFn),

  getSettlements: (mockFn) =>
    withFallback(() => api.get('/admin/settlements'), mockFn),

  markSettlementPaid: (id) =>
    api.patch(`/admin/settlements/${id}/pay`),

  getBankDetails: (mockFn) =>
    withFallback(() => api.get('/admin/bank-details'), mockFn),

  verifyBankDetails: (id, status) =>
    api.patch(`/admin/bank-details/${id}/verify`, { status }),

  getCommissionSettings: (mockFn) =>
    withFallback(() => api.get('/admin/commission/settings'), mockFn),

  updateCommissionRate: (restaurantId, rate) =>
    api.put(`/admin/commission/settings/${restaurantId}`, { rate }),

  // ── Order Cancellations & Rejections ───────────────────────
  getCancellations: (mockFn) =>
    withFallback(() => api.get('/admin/cancellations'), mockFn),

  getRejections: (mockFn) =>
    withFallback(() => api.get('/admin/rejections'), mockFn),

  // ── Roles & Permissions ────────────────────────────────────
  getRoles: (mockFn) =>
    withFallback(() => api.get('/admin/roles'), mockFn),

  updateRolePermissions: (roleId, permissions) =>
    api.put(`/admin/roles/${roleId}/permissions`, { permissions }),

  // ── Settings ───────────────────────────────────────────────
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (settings) => api.put('/admin/settings', settings),

  // ── Profile ────────────────────────────────────────────────
  getProfile: () => api.get('/admin/profile'),
  updateProfile: (data) => api.put('/admin/profile', data),
  changePassword: (current, newPassword) =>
    api.post('/admin/profile/change-password', { current_password: current, new_password: newPassword }),

  // ── Activity Logs ──────────────────────────────────────────
  getActivityLogs: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/admin/audit-logs${q ? '?' + q : ''}`);
  },
};

export default adminApi;
